import React from 'react';
import { Star, ShieldCheck, Zap, Leaf, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Section, Button, OrganicDivider } from './ui';
export const Hero = () => {
  return <Section bg="cream" className="pt-24 md:pt-32 pb-12 md:pb-24 overflow-hidden" data-id="element-117">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-118">
        <div className="space-y-8 relative z-10" data-id="element-119">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-herbal/10 text-herbalDark text-sm font-bold mb-2" data-id="element-120">
            <Leaf size={16} data-id="element-121" />
            <span data-id="element-122">Alternativa 100% Natural</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-petroleum leading-tight" data-id="element-123">
            Tu cuerpo no nació para depender de químicos todos los días...
          </h1>

          <h2 className="text-xl md:text-2xl font-medium text-gray-700 leading-relaxed" data-id="element-124">
            Recupera tu bienestar con el poder de las hierbas medicinales y{' '}
            <span className="font-bold text-herbalDark relative inline-block" data-id="element-125">
              más de 970 recetas naturales
              <svg className="absolute w-full h-3 -bottom-1 left-0 text-gold/50" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-126">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" data-id="element-127" />
              </svg>
            </span>{' '}
            fáciles de preparar desde casa.
          </h2>

          <p className="text-lg text-gray-600 italic" data-id="element-128">
            Descubre cómo miles de personas están reduciendo dolores,
            inflamación, estrés y malestares diarios usando remedios naturales
            ancestrales desde los primeros días.
          </p>

          <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-gray-600 bg-white p-4 rounded-2xl shadow-sm border border-gray-100" data-id="element-129">
            <div className="flex items-center gap-1 text-yellow-500" data-id="element-130">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" data-id="element-131" />)}
              <span className="text-gray-800 ml-1" data-id="element-132">
                +27.000 personas transformadas
              </span>
            </div>
            <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-300" data-id="element-133"></div>
            <div className="flex items-center gap-1" data-id="element-134">
              <ShieldCheck size={16} className="text-herbal" data-id="element-135" /> Pago 100% seguro
            </div>
            <div className="hidden sm:block w-1 h-1 rounded-full bg-gray-300" data-id="element-136"></div>
            <div className="flex items-center gap-1" data-id="element-137">
              <Zap size={16} className="text-gold" data-id="element-138" /> Acceso inmediato
            </div>
          </div>

          <div className="pt-4 flex flex-col items-start gap-3" data-id="element-139">
            <Button size="xl" className="w-full sm:w-auto shadow-glow-herbal group" data-id="element-140">
              ✨ SÍ, QUIERO EL MANUAL COMPLETO — $6,36 USD
            </Button>
            <p className="text-sm text-gray-500 flex items-center gap-2 ml-2" data-id="element-141">
              <ShieldCheck size={14} data-id="element-142" /> Garantía de 7 días · Acceso inmediato ·
              Sin riesgos
            </p>
          </div>
        </div>

        <div className="relative flex items-center justify-center" data-id="element-143">
          {/* Decorative background glow */}
          <div className="absolute inset-0 bg-gradient-to-tr from-herbal/20 to-gold/20 rounded-full blur-3xl opacity-60 animate-pulse-slow" data-id="element-144"></div>

          {/* Hero composition image */}
          <div className="relative w-full rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white" data-id="element-145">
            <img src="https://cdn.magicpatterns.com/uploads/pkbVLPxaG9yJM5UN5QkvWm/ChatGPT_Image_7_may_2026,_12_09_40_p.m.png" alt="El Gran Manual de Preparaciones Medicinales Naturales con bonos exclusivos" className="w-full h-full object-cover" data-id="element-146" />
          </div>

          {/* Floating leaves */}
          <Leaf className="absolute -top-4 right-4 text-herbal/40 w-12 h-12 animate-float delay-100 rotate-45" data-id="element-147" />
          <Leaf className="absolute -bottom-4 -left-4 text-herbal/30 w-10 h-10 animate-float delay-300 -rotate-12" data-id="element-148" />
        </div>
      </div>
    </Section>;
};
export const Identification = () => {
  return <>
      <OrganicDivider type="wave" color="fill-cream" data-id="element-149" />
      <Section bg="beige" className="pt-8" data-id="element-150">
        <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-151">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-petroleum mb-6" data-id="element-152">
            ¿Y si el problema no fuera tu cuerpo...{' '}
            <span className="italic text-herbalDark" data-id="element-153">
              sino que nunca te enseñaron cómo ayudarlo naturalmente?
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-154">
          <div className="space-y-6 text-lg text-gray-700" data-id="element-155">
            <p data-id="element-156">
              Sabemos cómo se siente. Estás{' '}
              <span className="font-bold" data-id="element-157">
                cansada de depender de medicamentos
              </span>{' '}
              que solo alivian el síntoma por unas horas, pero no resuelven la
              raíz del problema.
            </p>
            <p data-id="element-158">
              Los dolores constantes, la inflamación que no cede, esa ansiedad
              silenciosa y la pérdida de energía te hacen sentir{' '}
              <span className="italic" data-id="element-159">agotada física y emocionalmente</span>.
            </p>
            <p data-id="element-160">
              Es frustrante despertar cada día sintiendo que tu cuerpo no
              responde como antes, y tener miedo de que estos problemas empeoren
              con el tiempo.
            </p>
          </div>

          <div className="space-y-8" data-id="element-161">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-red-100" data-id="element-162">
              <h3 className="font-bold text-petroleum mb-4 flex items-center gap-2" data-id="element-163">
                <AlertTriangle className="text-urgency" size={20} data-id="element-164" />
                ¿Te identificas con esto?
              </h3>
              <ul className="space-y-3" data-id="element-165">
                {['Despertar todos los días con molestias y cansancio', 'Depender constantemente de pastillas', 'Gastar dinero en soluciones que no ayudan realmente', 'Sentir inflamación, estrés y agotamiento permanente', 'Tener miedo de que los problemas empeoren con el tiempo'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-600" data-id="element-166">
                    <XCircle className="text-urgency shrink-0 mt-0.5" size={18} data-id="element-167" />
                    <span data-id="element-168">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-herbal/5 p-6 rounded-3xl border border-herbal/20" data-id="element-169">
              <h3 className="font-bold text-petroleum mb-4 flex items-center gap-2" data-id="element-170">
                <Leaf className="text-herbal" size={20} data-id="element-171" />
                Existe un camino mejor:
              </h3>
              <ul className="space-y-3" data-id="element-172">
                {['Aprende preparaciones naturales fáciles desde casa', 'Apoya tu bienestar con ingredientes accesibles', 'Recupera sensación de alivio, energía y equilibrio'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-173">
                    <CheckCircle2 className="text-herbal shrink-0 mt-0.5" size={18} data-id="element-174" />
                    <span data-id="element-175">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </>;
};
export const CostOfInaction = () => {
  return <>
      <OrganicDivider type="wave" color="fill-beige" data-id="element-176" />
      <Section bg="cream" className="pt-8" data-id="element-177">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-178">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-petroleum mb-4" data-id="element-179">
            El problema no desaparece solo... <br data-id="element-180" />
            <span className="text-urgency" data-id="element-181">
              y el tiempo puede empeorarlo todo.
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-182">
          {[{
          title: 'CONSECUENCIA 1',
          desc: 'Dolores y cansancio cada vez más frecuentes.',
          icon: <AlertTriangle size={32} className="text-urgency mb-4" data-id="element-183" />
        }, {
          title: 'CONSECUENCIA 2',
          desc: 'Más dependencia de químicos y medicamentos.',
          icon: <ShieldCheck size={32} className="text-urgency mb-4" data-id="element-184" />
        }, {
          title: 'CONSECUENCIA 3',
          desc: 'Menos energía, menos bienestar y peor calidad de vida.',
          icon: <Zap size={32} className="text-urgency mb-4" data-id="element-185" />
        }].map((card, i) => <div key={i} className="bg-white p-8 rounded-[24px] shadow-sm border border-gray-100 hover:shadow-md transition-shadow text-center" data-id="element-186">
              <div className="flex justify-center" data-id="element-187">{card.icon}</div>
              <h3 className="font-bold text-sm text-gray-400 tracking-wider mb-3" data-id="element-188">
                {card.title}
              </h3>
              <p className="text-xl font-heading font-medium text-petroleum" data-id="element-189">
                {card.desc}
              </p>
            </div>)}
        </div>

        <div className="max-w-2xl mx-auto text-center bg-beige p-8 rounded-3xl border border-gold/20 relative overflow-hidden" data-id="element-190">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold/20 via-gold to-gold/20" data-id="element-191"></div>
          <p className="text-2xl font-heading font-bold text-petroleum italic" data-id="element-192">
            "Cada día que ignoras las señales de tu cuerpo... es un día más
            lejos del bienestar que mereces."
          </p>
        </div>
      </Section>
    </>;
};
export const Comparison = () => {
  return <Section bg="beige" data-id="element-193">
      <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-194">
        <h2 className="text-3xl md:text-4xl font-heading font-bold text-petroleum" data-id="element-195">
          No fallaste tú.{' '}
          <span className="text-herbalDark" data-id="element-196">Falló el enfoque.</span>
        </h2>
      </div>

      <div className="max-w-4xl mx-auto bg-white rounded-[24px] shadow-lg overflow-hidden border border-gray-100" data-id="element-197">
        <div className="grid grid-cols-2 bg-petroleum text-white p-4 md:p-6" data-id="element-198">
          <div className="font-bold text-center text-sm md:text-lg opacity-80" data-id="element-199">
            Soluciones tradicionales ❌
          </div>
          <div className="font-bold text-center text-sm md:text-lg text-gold" data-id="element-200">
            Manual Natural ✅
          </div>
        </div>

        <div className="divide-y divide-gray-100" data-id="element-201">
          {[['Solo atacan síntomas', 'Apoya el bienestar integral'], ['Generan dependencia', 'Alternativa natural'], ['Costosas a largo plazo', 'Acceso de por vida'], ['Difíciles de sostener', 'Fácil de aplicar en casa']].map((row, i) => <div key={i} className="grid grid-cols-2 p-4 md:p-6 hover:bg-gray-50 transition-colors" data-id="element-202">
              <div className="text-center text-gray-500 font-medium flex items-center justify-center gap-2" data-id="element-203">
                <XCircle size={16} className="text-red-400 hidden sm:block" data-id="element-204" />
                {row[0]}
              </div>
              <div className="text-center text-petroleum font-bold flex items-center justify-center gap-2" data-id="element-205">
                <CheckCircle2 size={16} className="text-herbal hidden sm:block" data-id="element-206" />
                {row[1]}
              </div>
            </div>)}
        </div>
      </div>
    </Section>;
};